import React, { useCallback } from 'react';
import { HelpDeskFormPresentation, ImgWrapper, PageWrapper } from './styled';
import { graphql, useMutation } from 'react-relay';
import type {
    addHelpDeskPageMutation,
    addHelpDeskPageMutation$data,
} from './__generated__/addHelpDeskPageMutation.graphql';
import { getCloudId, getHelpCenterAri } from 'util/meta';
import { showErrorFlag, showSuccessFlag } from 'state/actions/flags';
import { connect } from 'react-redux';
import messages from './messages';
import { useIntl } from 'react-intl-next';
import { HELP_DESK_EDIT_BASE_PATH } from 'view/common/constants';
import { FireScreenEventOnMount } from 'util/analytics/fire-screen-event';
import { addHelpDeskExperience } from '../experiences';
import { getErrorData } from 'util/analytics/listener';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { TRACK_EVENT_TYPE } from '@atlassian/analytics-web-react';
import { HelpDeskForm } from '../help-desk-form';
import { history } from 'util/history';
import Grid, { GridItem } from '@atlaskit/grid';
import chatBubbleEmoji from 'assets/images/chat-bubble-emoji.svg';
import { makeContactInfo } from '../utils';

const AddHelpDeskPage: React.FC<{ showErrorFlag: typeof showErrorFlag; showSuccessFlag: typeof showSuccessFlag }> = ({
    showErrorFlag: showErrorFlagAction,
    showSuccessFlag: showSuccessFlagAction,
}) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const [addHelpDesk] = useMutation<addHelpDeskPageMutation>(graphql`
        mutation addHelpDeskPageMutation($cloudId: ID!, $input: HelpCenterHelpDeskCreateInput!) {
            helpCenter(cloudId: $cloudId) {
                createHelpDesk(input: $input) {
                    success
                    errors {
                        message
                    }
                    helpDeskId
                }
            }
        }
    `);

    const onSubmit: React.ComponentProps<typeof HelpDeskForm>['onSubmit'] = useCallback(
        (data) => {
            addHelpDeskExperience.start() as unknown as void;
            return new Promise((resolve, reject) => {
                addHelpDesk({
                    variables: {
                        input: {
                            name: data.name,
                            description: data.description,
                            contactInfo: makeContactInfo(data.contactInfo),
                            helpCenterAri: getHelpCenterAri(),
                        },
                        cloudId: getCloudId(),
                    },
                    updater: (store, response) => {
                        if (response.helpCenter?.createHelpDesk?.success) {
                            const root = store.getRoot();
                            const helpCenter = root.getLinkedRecord('helpCenter', {
                                cloudId: getCloudId(),
                            });
                            const UHHelpCenter = helpCenter?.getLinkedRecord('helpCenterById', {
                                helpCenterAri: getHelpCenterAri(),
                            });

                            const helpdesksRecord = UHHelpCenter?.getLinkedRecord('helpDesks');

                            helpdesksRecord?.invalidateRecord();
                        }
                    },
                    onCompleted(response) {
                        if (response.helpCenter?.createHelpDesk?.success) {
                            return resolve(response);
                        }

                        const errors = response.helpCenter?.createHelpDesk?.errors;
                        const message = errors && errors.length > 0 ? errors[0].message : '';
                        return reject(message);
                    },
                    onError: (error) => reject(error),
                });
            })
                .then((response: addHelpDeskPageMutation$data) => {
                    const helpDeskId = response.helpCenter?.createHelpDesk?.helpDeskId;

                    const event = createAnalyticsEvent({
                        helpDeskId,
                        analyticsType: TRACK_EVENT_TYPE,
                        action: 'created',
                        actionSubject: 'HelpDesk',
                        descriptionLength: data.description.length,
                    });
                    event.fire();
                    showSuccessFlagAction(
                        messages.helpDeskAddSuccessTitle,
                        {
                            value: data.name,
                        },
                        messages.helpDeskSuccessDesc
                    );
                    addHelpDeskExperience.success() as unknown as void;
                    if (helpDeskId) {
                        history.push(`${HELP_DESK_EDIT_BASE_PATH}/${helpDeskId}/add-content`);
                    }
                })
                .catch((error) => {
                    showErrorFlagAction(messages.helpDeskAddErrorTitle, messages.helpDeskErrorDesc);

                    return addHelpDeskExperience.failure({
                        metadata: {
                            error: getErrorData(error),
                        },
                    });
                });
        },
        [addHelpDesk, createAnalyticsEvent, showErrorFlagAction, showSuccessFlagAction]
    );
    const { formatMessage } = useIntl();

    return (
        <PageWrapper>
            <Grid>
                <GridItem span={{ md: 3 }} start={{ md: 2 }}>
                    <ImgWrapper>
                        <HelpDeskFormPresentation src={chatBubbleEmoji} alt="" />
                    </ImgWrapper>
                </GridItem>
                <GridItem span={{ md: 5 }} start={{ md: 6 }}>
                    <HelpDeskForm
                        title={formatMessage(messages.addFormHeader)}
                        description={formatMessage(messages.addFormDesc)}
                        ctaText={formatMessage(messages.addHelpDeskFormSubmitButton)}
                        onSubmit={onSubmit}
                    />
                </GridItem>
            </Grid>
            <FireScreenEventOnMount />
        </PageWrapper>
    );
};

export default connect(null, {
    showErrorFlag,
    showSuccessFlag,
})(AddHelpDeskPage);
